import type { FC, MouseEvent } from 'react';
import { useEffect, useState } from 'react';

import type { HowlOptions } from 'howler';
import { Howl } from 'howler';
import type { ColorName } from 'styles/config/colors';

import { IconButton } from 'components/atoms/IconButton';
import { PlayIcon, StopIcon } from 'components/atoms/Icons';
import type { IconSize } from 'components/atoms/Icons/BaseIcon';
import { cn } from 'utils/cn';
import * as Styled from './TableCell.styled';

const PLAY_BUTTON_CLASSNAME = 'playAudioButton';

interface PlayAudioCellProps {
  className?: string;
  url: string;
  disabled?: boolean;
  onError?: (id: number, error: unknown) => void;
  options?: Omit<HowlOptions, 'src'>;
  iconColor?: ColorName;
  onPlayAudio?: (audio: Howl | undefined) => void;
  autoplay?: boolean;
  buttonClassName?: string;
  iconSize?: keyof typeof IconSize;
}

const PlayAudioCell: FC<PlayAudioCellProps> = ({
  className,
  url,
  disabled,
  onError,
  options = {},
  iconColor = undefined,
  onPlayAudio,
  autoplay = true,
  buttonClassName,
  iconSize = 'md',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio, setAudio] = useState<Howl>();
  const [currentUrl, setCurrentUrl] = useState('');

  const loadAudio = async () => {
    setIsLoading(true);
    const audioFile = new Howl({
      src: [url],
      ...options,
      autoplay,
      onloaderror: (id, error) => {
        setIsLoading(false);
        onError?.(id, error);
      },
      onload: () => setIsLoading(false),
      onplay: () => setIsPlaying(true),
      onstop: () => setIsPlaying(false),
      onend: () => setIsPlaying(false),
    });
    setAudio(audioFile);
    setCurrentUrl(url);
    return audioFile;
  };

  useEffect(() => {
    return () => {
      audio?.stop();
    };
  }, [audio]);

  const handlePlayAudio = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!audio || url !== currentUrl) {
      const audio = await loadAudio();
      onPlayAudio?.(audio);
    } else {
      audio.seek(0);
      audio.play();
      onPlayAudio?.(audio);
    }
  };

  const handleStop = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    audio?.stop();
  };

  return (
    <Styled.CenteredDiv className={className}>
      <IconButton
        aria-label={isPlaying ? 'Stop audio' : 'Play audio'}
        className={cn(PLAY_BUTTON_CLASSNAME, buttonClassName)}
        data-test-id="play-button"
        disabled={disabled}
        hoverTarget={false}
        iconSize={iconSize}
        loading={isLoading}
        onMouseDown={isPlaying ? handleStop : handlePlayAudio}
      >
        {isPlaying ? (
          <StopIcon color={iconColor} size={iconSize} />
        ) : (
          <PlayIcon color={iconColor} size={iconSize} />
        )}
      </IconButton>
    </Styled.CenteredDiv>
  );
};

export default PlayAudioCell;
